import { useState } from 'react';
import { apiFetch } from './config';
import { relationColor } from './trustShared';

// REFINE — the one correction gesture (DECISIONS.md 2026-07-26).
//
// Measured on 35 real recall suggestions: 23 were wrong, and every one of them
// had the right two endpoints and the wrong relation NAME. Nothing wanted a
// different target; nothing wanted a polarity flip. So this offers exactly one
// verb — retype — instead of the retype/flip/retarget triad that was seeded.
//
// It mints the corrected edge (inert, like every suggestion) and downvotes the
// original in one request. The wrong edge is not deleted: it keeps its history
// and its votes, and the community decides which reading survives. That is why
// refining is cheap — an inert edge costs nothing trust-wise until a human
// activates it, so correcting one never touches the activation boundary.
//
// The button and the panel are separate exports because EdgeSentence renders
// the button inside its right-hand action cluster while the panel needs a full
// row of its own — the cluster is a wrapping flex box that already overflows
// the Reader's ~300px case cards.

// Refine applies to a READING of how two claims relate. Provenance and
// plumbing are facts about where content came from, not interpretations —
// retyping a derived_from would corrupt the derivation chain, and there is no
// human judgment in an authored_by to correct. (`is_system` already covers
// authored_by/visible_to; these are the non-system ones that are still not
// readings. Found by looking at the render, not by review or tests.)
export const NOT_REFINABLE = new Set([
  'derived_from',
  'decomposed_from',
  'authored_by',
  'written_with',
  'visible_to',
  'tagged',
  'comments_on',
  'replies_to',
  'subscribes_to',
]);

// The destinations a correction almost always wants, in the order the
// measurement found them. Free text stays available — relation naming is a
// first-come namespace and downstream features tolerate any name (CLAUDE.md).
const COMMON = ['supports', 'qualifies', 'contradicts', 'restates', 'references', 'related_to'];

const HINTS = {
  supports: 'evidence for it, not the same claim',
  qualifies: 'it holds, with a bounded exception',
  contradicts: 'both cannot be true',
  restates: 'the same proposition in other words',
  references: 'points at it without arguing',
  related_to: 'connected, but not yet sharpened',
};

// DISPUTE — the sibling gesture to refine (2026-08-02). Refine says the name is
// wrong; dispute says the connection itself does not hold, and opens the edge's
// own page so it can be argued about like any other object. Styled as the same
// kind of quiet affordance, deliberately: neither is a destructive act, and both
// are cheap because nothing they mint is activated until a human votes.
export function DisputeButton({ onOpen, busy }) {
  return (
    <button
      className="edge-refine"
      onClick={onOpen}
      disabled={busy}
      title="Both ends fine but the link is wrong? Argue with the connection itself"
    >
      {busy ? 'opening…' : 'dispute'}
    </button>
  );
}

export function RefineButton({ onClick, open }) {
  return (
    <button
      className="edge-refine"
      onClick={onClick}
      aria-expanded={open}
      title="Wrong relation? Suggest the right one — the connection keeps its history"
    >
      refine
    </button>
  );
}

export default function RetypeEdge({ edge, onRetyped, onClose, compact }) {
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const apply = async (relationName) => {
    const name = String(relationName || '').trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/relation-instances/${edge.id}/retype`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relation_name: name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Could not refine this connection');
        setBusy(false);
        return;
      }
      setBusy(false);
      setCustom('');
      onClose();
      if (onRetyped) onRetyped();
    } catch (err) {
      setError('Could not refine this connection');
      setBusy(false);
    }
  };

  const options = COMMON.filter((n) => n !== edge.relation_name);

  return (
    <div className="edge-refine-panel">
      <div className="edge-refine-head">Right connection, wrong word? Pick what it really is:</div>
      <div className="edge-refine-options">
        {options.map((name) => (
          <button
            key={name}
            className="edge-refine-opt"
            disabled={busy}
            onClick={() => apply(name)}
            style={{ borderColor: relationColor(name) }}
            title={HINTS[name]}
          >
            <span style={{ color: relationColor(name), fontWeight: 600 }}>
              {name.replace(/_/g, ' ')}
            </span>
            {!compact && HINTS[name] ? (
              <span className="edge-refine-hint">{HINTS[name]}</span>
            ) : null}
          </button>
        ))}
      </div>
      <form
        className="edge-refine-custom"
        onSubmit={(e) => {
          e.preventDefault();
          apply(custom);
        }}
      >
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="or name it yourself"
          disabled={busy}
          aria-label="Custom relation name"
        />
        <button type="submit" disabled={busy || !custom.trim()}>
          use
        </button>
      </form>
      {error ? <div className="edge-refine-error">{error}</div> : null}
      <div className="edge-refine-foot">
        Your correction is added as a new unvoted connection, and this one is marked as not
        holding. Nothing is deleted.{' '}
        <button className="edge-refine-cancel" onClick={onClose} disabled={busy}>
          cancel
        </button>
      </div>
    </div>
  );
}
